import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import {
  Play, Pause, Plus, TrendingUp, TrendingDown, Users,
  Target, Clock, CheckCircle, AlertCircle, Settings,
  Calendar, Filter, Download, MoreVertical, Eye,
  StopCircle, Award, Zap, MessageSquare, TrendingUp as TrendUp,
  Sparkles, BarChart3, Activity, Star, ChevronRight,
  Building2, ChevronDown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { analyticsService } from '../lib/analyticsDataService';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';

const StatusPill = ({ status }) => {
  const statusConfig = {
    active: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-800',
      border: 'border-emerald-200',
      icon: <Play className="w-3 h-3" />,
      dot: 'bg-emerald-500'
    },
    running: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-800',
      border: 'border-emerald-200',
      icon: <Play className="w-3 h-3" />,
      dot: 'bg-emerald-500'
    },
    paused: {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      icon: <Pause className="w-3 h-3" />,
      dot: 'bg-amber-500'
    },
    completed: {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-200',
      icon: <CheckCircle className="w-3 h-3" />,
      dot: 'bg-slate-500'
    }
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${config.dot} animate-pulse`}></div>
      {config.icon}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export const PastExperimentCard = ({ experiment, onViewDetails }) => (
  <div className="group bg-white rounded-xl border border-slate-200 p-6 hover:border-slate-300 hover:shadow-lg transition-all duration-300 relative overflow-hidden">
    {/* Subtle gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 to-transparent pointer-events-none"></div>
    
    <div className="relative">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h4 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-700 transition-colors">
            {experiment.name}
          </h4>
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
            <Target className="w-4 h-4" />
            <span>Testing: {experiment.metric}</span>
          </div>
          <p className="text-xs text-slate-500">
            {experiment.campaignName || 'Global Test'}
          </p>
        </div>
        <StatusPill status={experiment.status} />
      </div>

      {experiment.improvement != null && experiment.winner && (
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <Award className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <div className="text-sm font-semibold text-emerald-900">
                Winner: Variant {experiment.winner}
              </div>
              <div className="text-lg font-bold text-emerald-700">
                +{(experiment.improvement ?? 0).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Users className="w-4 h-4" />
          <span className="font-medium">{experiment.participants?.toLocaleString() || 0}</span>
          <span>participants</span>
        </div>
        <button
          onClick={() => onViewDetails?.(experiment)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm group"
        >
          <span>View Results</span>
          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  </div>
);

class ABTestingDashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('ABTestingDashboard crashed:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6">
          <div className="text-center py-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200">
            <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Something went wrong</h3>
            <p className="text-slate-600">The A/B Testing dashboard failed to load.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AdminTenantFilterDropdown = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value);
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 rounded-lg text-sm font-medium text-indigo-700 cursor-pointer transition-all hover:bg-indigo-50 hover:border-indigo-300 min-w-[200px]"
      >
        <Building2 size={16} className="text-indigo-500" />
        <span className="flex-1 text-left truncate">{selectedOption?.label || 'Select Tenant'}</span>
        <ChevronDown size={16} className={`text-indigo-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto p-1">
            {options.map(option => (
              <button
                key={option.value}
                onClick={() => { onChange(option.value); setIsOpen(false); }}
                className={`block w-full px-3 py-2.5 text-left rounded-md text-sm transition-colors ${
                  value === option.value ? 'bg-indigo-50 text-indigo-600 font-semibold' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ABTestingDashboardContent = () => {
  // All hooks first
  const { user, hasFeature, currentPlan } = useAuth();
  
  const [activeView, setActiveView] = useState('overview');
  const [selectedTest, setSelectedTest] = useState(null);
  const [createStep, setCreateStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeExperiments, setActiveExperiments] = useState([]);
  const [pastExperiments, setPastExperiments] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
const [newExperiment, setNewExperiment] = useState({
  name: '',
  testType: '',
  metric: '',
  trafficSplit: 50,
  experimentType: 'campaign',
  campaignId: null,
  openingMode: 'template_vs_template', // NEW: 'template_vs_template' or 'template_vs_ai'
  variantA: { method: 'template', config: '' },
  variantB: { method: 'template', config: '' } 
});

  // Ref that always holds the latest loadExperimentData function.
  // Using a ref avoids a TDZ crash: loadExperimentData is a const defined AFTER the
  // early-return guards (hasFeature check), so if the component ever returned early
  // on a previous render (e.g. hasFeature briefly false during JWT refresh), the
  // useEffect([user]) closure would access an uninitialized binding and crash.
  const loadExperimentDataRef = useRef(null);

  // Global admin cross-tenant monitoring
  const isGlobalAdmin = user?.role === 'global_admin';
  const [adminTenants, setAdminTenants] = useState([]);
  const [selectedAdminTenant, setSelectedAdminTenant] = useState('all');
  const [adminExperiments, setAdminExperiments] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [selectedAdminExp, setSelectedAdminExp] = useState(null);
  const [adminExpDetail, setAdminExpDetail] = useState(null);
  const [adminExpDetailLoading, setAdminExpDetailLoading] = useState(false);

  // Fetch all tenants for global_admin dropdown
  useEffect(() => {
    if (!isGlobalAdmin) return;
    const fetchAdminTenants = async () => {
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('id, name')
          .or(VISIBLE_TENANTS_OR) // hide inactive/suspended from global_admin dropdown
          .order('name');
        if (error) throw error;
        setAdminTenants(data || []);
      } catch (err) {
        console.error('[GlobalAdmin] Error fetching tenants:', err);
      }
    };
    fetchAdminTenants();
  }, [isGlobalAdmin]);

  // Fetch cross-tenant experiments for global_admin
  useEffect(() => {
    if (!isGlobalAdmin) return;
    const fetchAdminExperiments = async () => {
      try {
        setAdminLoading(true);
        let query = supabase
          .from('experiments')
          .select('*, experiment_variants(*), campaigns(name)')
          .order('created_at', { ascending: false });
        if (selectedAdminTenant !== 'all') {
          query = query.eq('tenant_id', selectedAdminTenant);
        }
        const { data: experiments, error } = await query;
        if (error) throw error;
        if (!experiments || experiments.length === 0) {
          setAdminExperiments([]);
          return;
        }
        // Batch-fetch tenant names
        const tenantIds = [...new Set(experiments.map(e => e.tenant_id).filter(Boolean))];
        const { data: tenants } = await supabase
          .from('tenants')
          .select('id, name')
          .in('id', tenantIds);
        const tenantMap = Object.fromEntries((tenants || []).map(t => [t.id, t.name]));
        // Batch-fetch creator names
        const creatorIds = [...new Set(experiments.map(e => e.created_by_user_id).filter(Boolean))];
        let creatorMap = {};
        if (creatorIds.length > 0) {
          const { data: creators } = await supabase
            .from('users_profile')
            .select('id, first_name, last_name, email')
            .in('id', creatorIds);
          creatorMap = Object.fromEntries((creators || []).map(c => [c.id, c]));
        }
        // Batch-fetch real participant counts from experiment_results
        const expIds = experiments.map(e => e.id);
        const { data: resultRows } = await supabase
          .from('experiment_results')
          .select('experiment_id')
          .in('experiment_id', expIds);
        const participantMap = {};
        (resultRows || []).forEach(r => {
          participantMap[r.experiment_id] = (participantMap[r.experiment_id] || 0) + 1;
        });
        // Enrich experiments
        const enriched = experiments.map(exp => ({
          ...exp,
          tenantName: tenantMap[exp.tenant_id] || 'Unknown',
          creator: creatorMap[exp.created_by_user_id] || null,
          campaignName: exp.campaigns?.name || null,
          participantCount: participantMap[exp.id] || 0,
        }));
        setAdminExperiments(enriched);
      } catch (err) {
        console.error('[GlobalAdmin] Error fetching experiments:', err);
      } finally {
        setAdminLoading(false);
      }
    };
    fetchAdminExperiments();
  }, [isGlobalAdmin, selectedAdminTenant]);

  const openAdminExpDetail = async (exp) => {
    setSelectedAdminExp(exp);
    setAdminExpDetail(null);
    setAdminExpDetailLoading(true);
    try {
      const { data: results } = await supabase
        .from('experiment_results')
        .select('variant_id, metric_value, outcome_type')
        .eq('experiment_id', exp.id);
      const variantStats = {};
      (exp.experiment_variants || []).forEach(v => {
        const vResults = (results || []).filter(r => r.variant_id === v.id);
        const total = vResults.length;
        const avgMetric = total > 0
          ? vResults.reduce((sum, r) => sum + (r.metric_value || 0), 0) / total
          : null;
        variantStats[v.variant_name] = { total, avgMetric, config: v.configuration };
      });
      setAdminExpDetail({ variantStats, totalResults: (results || []).length });
    } catch (err) {
      console.error('[GlobalAdmin] Error fetching experiment detail:', err);
    } finally {
      setAdminExpDetailLoading(false);
    }
  };

  // Prevent scroll to top when modal is open
  useEffect(() => {
    if (activeView === 'create') {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [activeView]);
  
  // Initialize analytics service
  useEffect(() => {
    const initializeService = async () => {
      if (user) {
        try {
          await analyticsService.initialize(user);
          console.log('✅ Analytics service initialized for A/B testing');
        } catch (err) {
          console.error('Failed to initialize analytics service:', err);
          setError('Failed to initialize analytics service');
        }
      }
    };
    initializeService();
  }, [user]);

  useEffect(() => {
    if (user) {
      // Call through the ref so we never access loadExperimentData directly here —
      // that const is defined after early-return guards and would be in the TDZ if
      // the component ever returned early while user was still truthy.
      loadExperimentDataRef.current?.();
    }
  }, [user]);

  useEffect(() => {
    console.log('🧪 ABTestingDashboard mounted with plan:', currentPlan);
    return () => {
      console.log('🧪 ABTestingDashboard unmounting');
    };
  }, [currentPlan]);

  useEffect(() => {
    console.log('🔍 ABTestingDashboard RBAC check:', {
      currentPlan,
      hasFeature: hasFeature ? hasFeature('messageAbTesting') : 'hasFeature undefined',
      user: user ? 'logged in' : 'not logged in'
    });
  }, [currentPlan, hasFeature, user]);

  // Early returns after hooks
  if (!user) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin"></div>
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <p className="text-slate-600 font-medium">Loading A/B Testing Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!hasFeature || (!hasFeature('messageAbTesting') && user?.role !== 'global_admin')) {
    return (
      <div className="p-6">
        <div className="text-center py-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl border border-slate-200 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-4 left-4 w-20 h-20 bg-blue-200/30 rounded-full blur-2xl"></div>
          <div className="absolute bottom-4 right-4 w-32 h-32 bg-purple-200/30 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Sparkles className="w-12 h-12 text-white" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Unlock A/B Testing
            </h3>
            <p className="text-slate-600 mb-8 max-w-md mx-auto leading-relaxed">
              Optimize your AI's performance with data-driven experiments and boost your conversion rates
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-8">
              {[
                {
                  icon: Target,
                  title: "Smart Testing",
                  description: "Test different AI messages and approaches",
                  color: "blue"
                },
                {
                  icon: BarChart3,
                  title: "Statistical Analysis",
                  description: "Get reliable results with confidence intervals",
                  color: "emerald"
                },
                {
                  icon: TrendingUp,
                  title: "Conversion Boost",
                  description: "Improve performance by up to 40%",
                  color: "purple"
                }
              ].map((feature, index) => (
                <div key={index} className="group p-6 bg-white/70 backdrop-blur-sm rounded-xl border border-white/50 hover:border-slate-200 hover:shadow-lg transition-all duration-300">
                  <div className={`w-12 h-12 bg-gradient-to-br from-${feature.color}-500 to-${feature.color}-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
            
            <button className="group bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <span className="flex items-center gap-3">
                <Star className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Upgrade to Scale Plan
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Don't let a background data error interrupt the create/details modal — the user
  // could be mid-form and would lose all their work. Show the error only on the
  // overview screen where it's actually actionable.
  if (error && activeView === 'overview') {
    return (
      <div className="p-6">
        <div className="text-center py-16 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl border border-red-200">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-red-900 mb-2">Unable to load A/B Testing</h3>
          <p className="text-red-700 mb-6">{error}</p>
          <button 
            onClick={() => {
              setError('');
              loadExperimentData();
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // All the service functions (keeping original logic)
  const loadActiveExperiments = async () => {
    try {
      if (!analyticsService?.userId) {
        throw new Error('Analytics service not initialized');
      }
      console.log('📊 Loading active experiments via analytics service...');
      const experiments = await analyticsService.getActiveExperiments();
      console.log('✅ Active experiments loaded:', experiments.length);
      return experiments;
    } catch (err) {
      console.error('Error loading active experiments:', err);
      setError(err.message || 'Failed to load active experiments');
      return [];
    }
  };

  const loadCompletedExperiments = async () => {
    try {
      if (!analyticsService?.userId) {
        throw new Error('Analytics service not initialized');
      }
      console.log('📊 Loading completed experiments via analytics service...');
      const experiments = await analyticsService.getCompletedExperiments();
      console.log('✅ Completed experiments loaded:', experiments.length);
      return experiments;
    } catch (err) {
      console.error('Error loading completed experiments:', err);
      setError(err.message || 'Failed to load completed experiments');
      return [];
    }
  };

  const loadAvailableCampaigns = async () => {
    try {
      if (!analyticsService?.userId) {
        throw new Error('Analytics service not initialized');
      }
      console.log('📊 Loading campaigns for A/B testing via analytics service...');
      const campaigns = await analyticsService.getCampaignsForABTesting();
      console.log('✅ Campaigns loaded:', campaigns.length);
      return campaigns;
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(err.message || 'Failed to load campaigns');
      return [];
    }
  };

  const loadExperimentData = async () => {
    if (!analyticsService?.userId) {
      // Analytics service is still initializing (e.g. after a JWT token refresh).
      // Silently bail — the initialization useEffect will call us again once ready.
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      console.log('🧪 Loading A/B testing data via analytics service...');

      const [activeExp, completedExp, campaigns] = await Promise.all([
        loadActiveExperiments(),
        loadCompletedExperiments(),
        loadAvailableCampaigns()
      ]);

      setActiveExperiments(activeExp);
      setPastExperiments(completedExp);
      setAvailableCampaigns(campaigns);

      console.log('✅ A/B testing data loaded:', {
        active: activeExp.length,
        completed: completedExp.length,
        campaigns: campaigns.length
      });
    } catch (err) {
      console.error('Error loading A/B testing data:', err);
      setError('Failed to load experiment data');
    } finally {
      setLoading(false);
    }
  };
  // Keep the ref current so the useEffect([user]) above can always call the latest
  // version of this function without a direct reference (avoiding TDZ).
  loadExperimentDataRef.current = loadExperimentData;

  const handleCreateExperiment = async () => {
    try {
      if (!analyticsService?.userId) {
        throw new Error('Analytics service not initialized');
      }
      setLoading(true);
      console.log('Creating experiment via analytics service:', newExperiment);

      if (!newExperiment.name || !newExperiment.testType || !newExperiment.metric) {
        throw new Error('Please fill in all required fields');
      }

      if (newExperiment.experimentType === 'campaign' && !newExperiment.campaignId) {
        throw new Error('Please select a campaign for campaign-specific tests');
      }

      const result = await analyticsService.createExperiment({
        name: newExperiment.name,
        testType: newExperiment.testType,
        metric: newExperiment.metric,
        trafficSplit: newExperiment.trafficSplit,
        experimentType: newExperiment.experimentType,
        campaignId: newExperiment.campaignId,
        variantA: newExperiment.variantA,
        variantB: newExperiment.variantB,
        minimumSampleSize: 100
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create experiment');
      }

      console.log('✅ Experiment created successfully via analytics service');

      setActiveView('overview');
      setCreateStep(1);
setNewExperiment({
  name: '',
  testType: '',
  metric: '',
  trafficSplit: 50,
  experimentType: 'campaign',
  campaignId: null,
  openingMode: 'template_vs_template',
  variantA: { method: 'template', config: '' },
  variantB: { method: 'template', config: '' }
});

      await loadExperimentData();
    } catch (err) {
      console.error('Error creating experiment:', err);
      setError(err.message || 'Failed to create experiment');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExperimentStatus = async (experimentId, newStatus) => {
    try {
      if (!analyticsService?.userId) {
        throw new Error('Analytics service not initialized');
      }
      console.log(`Updating experiment ${experimentId} to ${newStatus} via analytics service`);

      const result = await analyticsService.updateExperimentStatus(experimentId, newStatus);

      if (!result.success) {
        throw new Error(result.error || `Failed to ${newStatus} experiment`);
      }

      console.log(`✅ Experiment ${experimentId} ${newStatus} via analytics service`);
      await loadExperimentData();
    } catch (err) {
      console.error(`Error updating experiment status:`, err);
      setError(`Failed to ${newStatus} experiment: ${err.message}`);
    }
  };

  const handleViewDetails = async (experiment) => {
    try {
      if (!analyticsService?.userId) {
        throw new Error('Analytics service not initialized');
      }
      setLoading(true);
      console.log('Loading experiment details via analytics service:', experiment.id);

      const metrics = await analyticsService.calculateExperimentMetrics(experiment.id);

      const mockPerformanceData = [
        { day: 'Day 1', variantA: 12.5, variantB: 15.2 },
        { day: 'Day 2', variantA: 13.1, variantB: 16.8 },
        { day: 'Day 3', variantA: 12.9, variantB: 17.1 },
        { day: 'Day 4', variantA: 13.4, variantB: 17.5 },
        { day: 'Day 5', variantA: 13.2, variantB: 18.2 }
      ];

      setSelectedTest({
        ...experiment,
        performanceOverTime: mockPerformanceData,
        ...metrics,
        confidence_level: metrics.confidence,
        total_participants: metrics.totalParticipants,
        variants: metrics.variants,
        leader: metrics.leader
      });

      setPerformanceData(mockPerformanceData);
      setActiveView('details');
    } catch (err) {
      console.error('Error loading experiment details:', err);
      setError(`Failed to load experiment details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const ConfidenceIndicator = ({ confidence }) => {
    if (confidence >= 95) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <span className="text-xs font-semibold text-emerald-700">Significant</span>
        </div>
      );
    } else if (confidence >= 70) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-full">
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
          <Activity className="w-4 h-4 text-amber-600" />
          <span className="text-xs font-semibold text-amber-700">Trending</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
          <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
          <Clock className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-semibold text-slate-600">Collecting Data</span>
        </div>
      );
    }
  };

  const ExperimentCard = ({ experiment }) => (
    <div className="group bg-white rounded-xl border border-slate-200 p-6 hover:border-slate-300 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/30 to-transparent pointer-events-none"></div>
      
      <div className="relative">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h3 className="font-bold text-slate-900 mb-2 text-lg group-hover:text-blue-700 transition-colors">
              {experiment.name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="w-3 h-3 text-blue-600" />
              </div>
              <span className="font-medium">Testing: {experiment.metric}</span>
            </div>
            <p className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full inline-block">
              {experiment.experimentType === 'global' ? 'Global Test' : `Campaign: ${experiment.campaignName}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={experiment.status} />
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <MoreVertical className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
        </div>

        {experiment.leader && (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-emerald-900">
                  Variant {experiment.leader.variant} Leading
                </div>
                <div className="text-xl font-bold text-emerald-700">
                  +{experiment.leader.improvement}% improvement
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-xs text-slate-500 mb-1">Participants</p>
            <p className="font-bold text-slate-900">{experiment.participants?.toLocaleString() || 0}</p>
          </div>
          <div className="text-center">
            <div className="mb-2">
              <ConfidenceIndicator confidence={experiment.confidence || 0} />
            </div>
            <p className="text-xs text-slate-500 mb-1">Confidence</p>
            <p className="font-bold text-slate-900">{experiment.confidence || 0}%</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-xs text-slate-500 mb-1">Progress</p>
            <div className="w-full bg-slate-200 rounded-full h-2 mb-1">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${Math.min(experiment.confidence || 0, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex gap-2">
            <button 
              onClick={() => handleViewDetails(experiment)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm hover:shadow-md"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>
            {(experiment.status === 'running' || experiment.status === 'active') && (
              <button 
                onClick={() => handleUpdateExperimentStatus(experiment.id, 'paused')}
                className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium transition-colors"
              >
                <Pause className="w-4 h-4" />
                Pause
              </button>
            )}
            {experiment.status === 'paused' && (
              <button 
                onClick={() => handleUpdateExperimentStatus(experiment.id, 'active')}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg text-sm font-medium transition-colors"
              >
                <Play className="w-4 h-4" />
                Resume
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400 font-medium">
            {experiment.startDate && new Date(experiment.startDate).toLocaleDateString()}
            {experiment.endDate && ` - ${new Date(experiment.endDate).toLocaleDateString()}`}
          </span>
        </div>
      </div>
    </div>
  );

  // Enhanced Create Experiment Modal (keeping original structure but with better styling)
  const CreateExperimentModal = () => (
<div 
  className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
>
  <div 
    className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl"
    onClick={(e) => e.stopPropagation()}
  >
        {/* Header */}
        <div className="border-b border-slate-200 p-8 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Create New Experiment</h2>
              <p className="text-slate-600 mt-1">Set up a data-driven test to optimize your AI performance</p>
            </div>
            <button 
              onClick={() => {
                setActiveView('overview');
                setCreateStep(1);
                setNewExperiment({
                  name: '',
                  testType: '',
                  metric: '',
                  trafficSplit: 50,
                  experimentType: 'campaign',
                  campaignId: null,
                  openingMode: 'template_vs_template', // ADD THIS
                  variantA: { method: 'template', config: '' },
                  variantB: { method: 'template', config: '' } 
                });
              }}
              className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Enhanced Progress Steps */}
          <div className="flex items-center space-x-4">
            {[
              { number: 1, title: 'Define Basics', description: 'Set up experiment parameters' },
              { number: 2, title: 'Configure Variants', description: 'Design test variations' },
              { number: 3, title: 'Review & Launch', description: 'Confirm and start test' }
            ].map((step, index) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold transition-all ${
                    step.number <= createStep 
                      ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-lg' 
                      : 'bg-slate-200 text-slate-600'
                  }`}>
                    {step.number}
                  </div>
                  <div>
                    <div className={`font-semibold text-sm ${
                      step.number <= createStep ? 'text-slate-900' : 'text-slate-500'
                    }`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-slate-500">{step.description}</div>
                  </div>
                </div>
                {index < 2 && (
                  <div className={`flex-1 h-1 mx-4 rounded-full ${
                    step.number < createStep ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-slate-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content - keeping original logic but with enhanced styling */}
        <div className="p-8">
          {createStep === 1 && (
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Experiment Name
                </label>
                <input
                  type="text"
                  autoFocus
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-slate-900 placeholder-slate-400"
                  placeholder="e.g., Q2 Opening Line Optimization Test"
                  value={newExperiment.name}
                  onChange={(e) => setNewExperiment({...newExperiment, name: e.target.value})}
                />
              </div>

              {/* Experiment Type */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-4">
                  Experiment Scope
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setNewExperiment({...newExperiment, experimentType: 'campaign', campaignId: null})}
                    className={`group p-6 border-2 rounded-2xl text-left transition-all duration-300 ${
                      newExperiment.experimentType === 'campaign'
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-lg' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      newExperiment.experimentType === 'campaign' 
                        ? 'bg-blue-100' 
                        : 'bg-slate-100 group-hover:bg-blue-100'
                    } transition-colors`}>
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="font-semibold text-slate-900 mb-2">Campaign-Specific</div>
                    <div className="text-sm text-slate-600">Test within a specific campaign</div>
                  </button>
                  <button
                    onClick={() => setNewExperiment({...newExperiment, experimentType: 'global', campaignId: null})}
                    className={`group p-6 border-2 rounded-2xl text-left transition-all duration-300 ${
                      newExperiment.experimentType === 'global'
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-lg' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      newExperiment.experimentType === 'global' 
                        ? 'bg-blue-100' 
                        : 'bg-slate-100 group-hover:bg-blue-100'
                    } transition-colors`}>
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="font-semibold text-slate-900 mb-2">Global</div>
                    <div className="text-sm text-slate-600">Test across all active campaigns</div>
                  </button>
                </div>
              </div>

              {/* Campaign Selection */}
              {newExperiment.experimentType === 'campaign' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    Select Campaign
                  </label>
                  <select 
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white text-slate-900"
                    value={newExperiment.campaignId || ''}
                    onChange={(e) => setNewExperiment({...newExperiment, campaignId: e.target.value || null})}
                  >
                    <option value="">Choose a campaign...</option>
                    {availableCampaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name} {campaign.is_active ? '(Active)' : '(Inactive)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-4">
                  What are you testing?
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { 
                      id: 'opening', 
                      label: 'Opening Message', 
                      icon: MessageSquare, 
                      desc: 'Test different conversation starters',
                      badge: 'CRITICAL',
                      badgeColor: 'bg-red-100 text-red-700'
                    },
                    { 
                      id: 'tone', 
                      label: 'AI Tone & Style', 
                      icon: Settings, 
                      desc: 'Compare personality and communication styles',
                      badge: 'HIGH IMPACT',
                      badgeColor: 'bg-orange-100 text-orange-700'
                    }
                  ].map(({ id, label, icon: Icon, desc, badge, badgeColor }) => (
                    <button
                      key={id}
                      onClick={() => setNewExperiment({...newExperiment, testType: id})}
                      className={`group p-6 border-2 rounded-2xl text-left transition-all duration-300 relative ${
                        newExperiment.testType === id 
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-lg' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                      }`}
                    >
                      <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold ${badgeColor}`}>
                        {badge}
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                        newExperiment.testType === id 
                          ? 'bg-blue-100' 
                          : 'bg-slate-100 group-hover:bg-blue-100'
                      } transition-colors`}>
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="font-semibold text-slate-900 mb-2">{label}</div>
                      <div className="text-sm text-slate-600 pr-16">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-4">
                  Primary Success Metric
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: 'conversion', label: 'Hot Lead Conversion Rate', icon: TrendUp, desc: 'Track qualified prospects' },
                    { id: 'reply', label: 'Reply Rate', icon: MessageSquare, desc: 'Measure engagement levels' },
                    { id: 'engagement', label: 'Engagement Score', icon: Zap, desc: 'Overall interaction quality' },
                    { id: 'response', label: 'Response Time', icon: Clock, desc: 'Speed of lead response' }
                  ].map(({ id, label, icon: Icon, desc }) => (
                    <button
                      key={id}
                      onClick={() => setNewExperiment({...newExperiment, metric: id})}
                      className={`group p-6 border-2 rounded-2xl text-left transition-all duration-300 ${
                        newExperiment.metric === id 
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-lg' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                        newExperiment.metric === id 
                          ? 'bg-blue-100' 
                          : 'bg-slate-100 group-hover:bg-blue-100'
                      } transition-colors`}>
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="font-semibold text-slate-900 mb-2">{label}</div>
                      <div className="text-sm text-slate-600">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-4">
                  Traffic Split
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center space-x-6 mb-4">
                    <div className="flex-1">
                      <input
                        type="range"
                        min="10"
                        max="90"
                        value={newExperiment.trafficSplit}
                        onChange={(e) => setNewExperiment({...newExperiment, trafficSplit: parseInt(e.target.value)})}
                        className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${newExperiment.trafficSplit}%, #e2e8f0 ${newExperiment.trafficSplit}%, #e2e8f0 100%)`
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <div className="flex-1 bg-blue-100 border border-blue-200 rounded-xl p-4 text-center">
                      <div className="text-blue-600 font-semibold mb-1">Variant A (Control)</div>
                      <div className="text-2xl font-bold text-blue-700">{newExperiment.trafficSplit}%</div>
                    </div>
                    <div className="flex-1 bg-emerald-100 border border-emerald-200 rounded-xl p-4 text-center">
                      <div className="text-emerald-600 font-semibold mb-1">Variant B (Test)</div>
                      <div className="text-2xl font-bold text-emerald-700">{100 - newExperiment.trafficSplit}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {createStep === 2 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Configure Your Variants</h3>
                <p className="text-slate-600 text-lg">Set up what each group of leads will experience</p>
              </div>
              
              {/* Mode Selector */}
              {newExperiment.testType === 'opening' && (
                <div className="col-span-2 mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-4 text-center">
                    What would you like to test?
                  </label>
                  <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                    <button
                      type="button"
                      onClick={() => setNewExperiment({
                        ...newExperiment, 
                        openingMode: 'template_vs_template',
                        variantA: { method: 'template', config: newExperiment.variantA.config },
                        variantB: { method: 'template', config: newExperiment.variantB.config }
                      })}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        newExperiment.openingMode === 'template_vs_template'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="font-semibold text-slate-900 mb-1">Template vs Template</div>
                      <div className="text-sm text-slate-600">Compare two specific messages</div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setNewExperiment({
                        ...newExperiment, 
                        openingMode: 'template_vs_ai',
                        variantA: { method: 'template', config: newExperiment.variantA.config },
                        variantB: { method: 'ai', config: null }
                      })}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        newExperiment.openingMode === 'template_vs_ai'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="font-semibold text-slate-900 mb-1">Template vs AI</div>
                      <div className="text-sm text-slate-600">Compare template to AI generation</div>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-8">
                {/* Variant A */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h4 className="text-xl font-bold text-blue-900 mb-1">Variant A (Control)</h4>
                      <span className="text-sm text-blue-700 bg-blue-200 px-3 py-1 rounded-full">
                        {newExperiment.trafficSplit}% of traffic
                      </span>
                    </div>
                    <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
                      <span className="text-blue-700 font-bold text-xl">A</span>
                    </div>
                  </div>
                  
                  

                  {newExperiment.testType === 'opening' && (
                    <div>
                      <label className="block text-sm font-semibold text-blue-800 mb-3">Opening Message</label>
                      <textarea
                        className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900 placeholder-slate-400"
                        rows="4"
                        placeholder="Hi {first_name}, I noticed your property listing..."
                        value={newExperiment.variantA.config}
                        onChange={(e) => setNewExperiment({
                          ...newExperiment,
                          variantA: { method: 'template', config: e.target.value }
                        })}
                      />
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-xs font-semibold text-blue-700 mb-1.5">Insert placeholder:</div>
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {[
                            { v: '{first_name}', desc: 'Smart name — "Dr. Bauer" or "Tom"' },
                            { v: '{last_name}', desc: 'Last name' },
                            { v: '{name}', desc: 'Full name' },
                            { v: '{company}', desc: 'Company' },
                            { v: '{job_title}', desc: 'Job title' },
                          ].map(({ v, desc }) => (
                            <button
                              key={v}
                              type="button"
                              title={desc}
                              onClick={() => setNewExperiment({
                                ...newExperiment,
                                variantA: { method: 'template', config: (newExperiment.variantA.config || '') + v }
                              })}
                              className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-blue-600">
                          <span className="font-semibold">{'{first_name}'}</span> is credential-aware — addresses doctors as "Dr. Smith", everyone else by first name.
                        </p>
                      </div>
                      <div className="mt-3 p-4 bg-white border border-blue-200 rounded-xl">
                        <div className="text-sm font-semibold text-blue-800 mb-2">Preview:</div>
                        <div className="text-sm text-slate-700 italic">
                          {newExperiment.variantA.config || "Hi {first_name}, I noticed your property listing..."}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {newExperiment.testType === 'tone' && (
                    <div>
                      <label className="block text-sm font-semibold text-blue-800 mb-3">AI Tone & Communication Style</label>
                      <select 
                        className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                        value={newExperiment.variantA.config}
                        onChange={(e) => setNewExperiment({
                          ...newExperiment, 
                          variantA: { config: e.target.value }
                        })}
                      >
                        <option value="">Select communication style...</option>
                        <option value="professional">Professional & Direct</option>
                        <option value="friendly">Friendly & Approachable</option>
                        <option value="casual">Casual & Conversational</option>
                        <option value="consultative">Consultative & Expert</option>
                        <option value="urgent">Urgent & Action-Oriented</option>
                      </select>
                      <div className="mt-4 p-4 bg-white border border-blue-200 rounded-xl">
                        <div className="text-sm font-semibold text-blue-800 mb-2">Preview:</div>
                        <div className="text-sm text-slate-700 italic">
                          {newExperiment.variantA.config === 'professional' && "Hi John, I specialize in properties like yours and would like to discuss your goals."}
                          {newExperiment.variantA.config === 'friendly' && "Hi John! I saw your listing and thought we might be a great fit to work together."}
                          {newExperiment.variantA.config === 'casual' && "Hey John - love what you've done with the property! Mind if we chat?"}
                          {newExperiment.variantA.config === 'consultative' && "John, based on market trends in your area, I have insights that could benefit your situation."}
                          {newExperiment.variantA.config === 'urgent' && "John - time-sensitive opportunity for your property. Can we talk today?"}
                          {!newExperiment.variantA.config && "Select a tone to see preview"}
                        </div>
                      </div>
                    </div>
                  )}

                  {newExperiment.testType === 'sequence' && (
                    <div>
                      <label className="block text-sm font-semibold text-blue-800 mb-3">Message Sequence</label>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-2">Message 1 (Initial)</label>
                          <textarea
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900 text-sm"
                            rows="2"
                            placeholder="Hi {name}, I noticed your listing..."
                            value={newExperiment.variantA.message1 || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantA: { ...newExperiment.variantA, message1: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-2">Message 2 (Follow-up after 3 days)</label>
                          <textarea
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900 text-sm"
                            rows="2"
                            placeholder="Following up on my previous message..."
                            value={newExperiment.variantA.message2 || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantA: { ...newExperiment.variantA, message2: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {newExperiment.testType === 'timing' && (
                    <div>
                      <label className="block text-sm font-semibold text-blue-800 mb-3">Follow-up Timing</label>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-2">First Follow-up Delay</label>
                          <select 
                            className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                            value={newExperiment.variantA.firstDelay || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantA: { ...newExperiment.variantA, firstDelay: e.target.value }
                            })}
                          >
                            <option value="">Select timing...</option>
                            <option value="1day">1 Day</option>
                            <option value="2days">2 Days</option>
                            <option value="3days">3 Days</option>
                            <option value="1week">1 Week</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-2">Second Follow-up Delay</label>
                          <select 
                            className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-900"
                            value={newExperiment.variantA.secondDelay || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantA: { ...newExperiment.variantA, secondDelay: e.target.value }
                            })}
                          >
                            <option value="">Select timing...</option>
                            <option value="3days">3 Days after first</option>
                            <option value="1week">1 Week after first</option>
                            <option value="2weeks">2 Weeks after first</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Reset to Current Live Settings
                  </button>
                </div>

                {/* Variant B */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h4 className="text-xl font-bold text-emerald-900 mb-1">Variant B (Test)</h4>
                      <span className="text-sm text-emerald-700 bg-emerald-200 px-3 py-1 rounded-full">
                        {100 - newExperiment.trafficSplit}% of traffic
                      </span>
                    </div>
                    <div className="w-12 h-12 bg-emerald-200 rounded-xl flex items-center justify-center">
                      <span className="text-emerald-700 font-bold text-xl">B</span>
                    </div>
                  </div>
                  
                  {newExperiment.testType === 'opening' && (
  <div>
    {newExperiment.openingMode === 'template_vs_template' ? (
      <>
        <label className="block text-sm font-semibold text-emerald-800 mb-3">Opening Message</label>
        <textarea
          className="w-full px-4 py-3 border border-emerald-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 placeholder-slate-400"
          rows="4"
          placeholder="Hey {first_name}! Saw your listing and wanted to reach out..."
          value={newExperiment.variantB.config}
          onChange={(e) => setNewExperiment({
            ...newExperiment,
            variantB: { method: 'template', config: e.target.value }
          })}
        />
        <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="text-xs font-semibold text-emerald-700 mb-1.5">Insert placeholder:</div>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {[
              { v: '{first_name}', desc: 'Smart name — "Dr. Bauer" or "Tom"' },
              { v: '{last_name}', desc: 'Last name' },
              { v: '{name}', desc: 'Full name' },
              { v: '{company}', desc: 'Company' },
              { v: '{job_title}', desc: 'Job title' },
            ].map(({ v, desc }) => (
              <button
                key={v}
                type="button"
                title={desc}
                onClick={() => setNewExperiment({
                  ...newExperiment,
                  variantB: { method: 'template', config: (newExperiment.variantB.config || '') + v }
                })}
                className="px-2 py-0.5 bg-white border border-emerald-300 rounded text-xs font-mono text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                {v}
              </button>
            ))}
          </div>
          <p className="text-xs text-emerald-600">
            <span className="font-semibold">{'{first_name}'}</span> is credential-aware — addresses doctors as "Dr. Smith", everyone else by first name.
          </p>
        </div>
        <div className="mt-3 p-4 bg-white border border-emerald-200 rounded-xl">
          <div className="text-sm font-semibold text-emerald-800 mb-2">Preview:</div>
          <div className="text-sm text-slate-700 italic">
            {newExperiment.variantB.config || "Hey {first_name}! Saw your listing and wanted to reach out..."}
          </div>
        </div>
      </>
    ) : (
      <div className="bg-white border-2 border-emerald-300 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="w-6 h-6 text-emerald-600" />
          <div className="font-semibold text-emerald-900">AI Generation Enabled</div>
        </div>
        <div className="text-sm text-slate-600 leading-relaxed">
          This variant will use your campaign's configured AI instructions to generate personalized messages for each lead.
        </div>
        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="text-xs font-semibold text-emerald-800 mb-1">Configuration:</div>
          <div className="text-xs text-emerald-700">
            ✓ Campaign AI instructions<br/>
            ✓ Lead personalization<br/>
            ✓ Knowledge base context
          </div>
        </div>
      </div>
    )}
  </div>
)}
                  
                  {newExperiment.testType === 'tone' && (
                    <div>
                      <label className="block text-sm font-semibold text-emerald-800 mb-3">AI Tone & Communication Style</label>
                      <select 
                        className="w-full px-4 py-3 border border-emerald-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900"
                        value={newExperiment.variantB.config}
                        onChange={(e) => setNewExperiment({
                          ...newExperiment, 
                          variantB: { config: e.target.value }
                        })}
                      >
                        <option value="">Select communication style...</option>
                        <option value="professional">Professional & Direct</option>
                        <option value="friendly">Friendly & Approachable</option>
                        <option value="casual">Casual & Conversational</option>
                        <option value="consultative">Consultative & Expert</option>
                        <option value="urgent">Urgent & Action-Oriented</option>
                      </select>
                      <div className="mt-4 p-4 bg-white border border-emerald-200 rounded-xl">
                        <div className="text-sm font-semibold text-emerald-800 mb-2">Preview:</div>
                        <div className="text-sm text-slate-700 italic">
                          {newExperiment.variantB.config === 'professional' && "Hi John, I specialize in properties like yours and would like to discuss your goals."}
                          {newExperiment.variantB.config === 'friendly' && "Hi John! I saw your listing and thought we might be a great fit to work together."}
                          {newExperiment.variantB.config === 'casual' && "Hey John - love what you've done with the property! Mind if we chat?"}
                          {newExperiment.variantB.config === 'consultative' && "John, based on market trends in your area, I have insights that could benefit your situation."}
                          {newExperiment.variantB.config === 'urgent' && "John - time-sensitive opportunity for your property. Can we talk today?"}
                          {!newExperiment.variantB.config && "Select a tone to see preview"}
                        </div>
                      </div>
                    </div>
                  )}

                  {newExperiment.testType === 'sequence' && (
                    <div>
                      <label className="block text-sm font-semibold text-emerald-800 mb-3">Message Sequence</label>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-emerald-700 mb-2">Message 1 (Initial)</label>
                          <textarea
                            className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 text-sm"
                            rows="2"
                            placeholder="Hey {name}! Saw your property listing..."
                            value={newExperiment.variantB.message1 || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantB: { ...newExperiment.variantB, message1: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-emerald-700 mb-2">Message 2 (Follow-up after 1 day)</label>
                          <textarea
                            className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 text-sm"
                            rows="2"
                            placeholder="Quick follow-up - are you interested?"
                            value={newExperiment.variantB.message2 || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantB: { ...newExperiment.variantB, message2: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-emerald-700 mb-2">Message 3 (Final follow-up after 3 more days)</label>
                          <textarea
                            className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 text-sm"
                            rows="2"
                            placeholder="Last chance to connect..."
                            value={newExperiment.variantB.message3 || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantB: { ...newExperiment.variantB, message3: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {newExperiment.testType === 'timing' && (
                    <div>
                      <label className="block text-sm font-semibold text-emerald-800 mb-3">Follow-up Timing</label>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-emerald-700 mb-2">First Follow-up Delay</label>
                          <select 
                            className="w-full px-4 py-3 border border-emerald-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900"
                            value={newExperiment.variantB.firstDelay || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantB: { ...newExperiment.variantB, firstDelay: e.target.value }
                            })}
                          >
                            <option value="">Select timing...</option>
                            <option value="1day">1 Day</option>
                            <option value="2days">2 Days</option>
                            <option value="3days">3 Days</option>
                            <option value="1week">1 Week</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-emerald-700 mb-2">Second Follow-up Delay</label>
                          <select 
                            className="w-full px-4 py-3 border border-emerald-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900"
                            value={newExperiment.variantB.secondDelay || ''}
                            onChange={(e) => setNewExperiment({
                              ...newExperiment, 
                              variantB: { ...newExperiment.variantB, secondDelay: e.target.value }
                            })}
                          >
                            <option value="">Select timing...</option>
                            <option value="1day">1 Day after first</option>
                            <option value="3days">3 Days after first</option>
                            <option value="1week">1 Week after first</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {createStep === 3 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Review & Launch</h3>
                <p className="text-slate-600 text-lg">Double-check your settings before starting the test</p>
              </div>
              
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-8">
                <h4 className="text-xl font-bold text-slate-900 mb-6">Experiment Summary</h4>
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {[
                    { label: 'Name', value: newExperiment.name },
                    { label: 'Scope', value: newExperiment.experimentType },
                    { label: 'Testing', value: newExperiment.testType },
                    { label: 'Metric', value: newExperiment.metric },
                    { label: 'Split', value: `${newExperiment.trafficSplit}% / ${100 - newExperiment.trafficSplit}%` },
                    { label: 'Campaign', value: newExperiment.campaignId ? 
                        availableCampaigns.find(c => c.id === newExperiment.campaignId)?.name || 'Unknown' : 
                        'Global Test' }
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="text-sm font-medium text-slate-600 mb-1">{label}</div>
                      <div className="font-semibold text-slate-900">{value}</div>
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <h5 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center">
                        <span className="text-blue-700 font-bold">A</span>
                      </div>
                      Variant A Configuration
                    </h5>
                    <div className="bg-white border border-blue-200 rounded-lg p-4 text-sm text-slate-700">
                      {newExperiment.variantA.config || 'Using current live settings'}
                    </div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                    <h5 className="font-bold text-emerald-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-emerald-200 rounded-lg flex items-center justify-center">
                        <span className="text-emerald-700 font-bold">B</span>
                      </div>
                      Variant B Configuration
                    </h5>
                    <div className="bg-white border border-emerald-200 rounded-lg p-4 text-sm text-slate-700">
                      {newExperiment.variantB.config || 'Not configured'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-blue-900 mb-2">Ready to launch!</h5>
                    <p className="text-blue-800 leading-relaxed">
                      This experiment will automatically start collecting data from new leads. 
                      You can pause or end it at any time from the dashboard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with enhanced buttons */}
        <div className="border-t border-slate-200 p-8 bg-slate-50 flex justify-between">
          <button
            onClick={() => createStep > 1 ? setCreateStep(createStep - 1) : setActiveView('overview')}
            className="px-6 py-3 border border-slate-300 rounded-xl text-slate-700 hover:bg-slate-100 font-medium transition-colors"
          >
            {createStep > 1 ? 'Previous' : 'Cancel'}
          </button>
          
          {createStep < 3 ? (
            <button
              onClick={() => setCreateStep(createStep + 1)}
              disabled={!newExperiment.name || !newExperiment.testType || !newExperiment.metric || 
                       (newExperiment.experimentType === 'campaign' && !newExperiment.campaignId)}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all"
            >
              Next Step
            </button>
          ) : (
            <button
              onClick={handleCreateExperiment}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 font-medium transition-all flex items-center gap-2"
            >
              <Play className="w-5 h-5" />
              {loading ? 'Creating...' : 'Launch Experiment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Enhanced Experiment Details View
  const ExperimentDetails = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="p-8 space-y-8">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setActiveView('overview')}
            className="flex items-center gap-3 text-blue-600 hover:text-blue-700 font-semibold text-lg group transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <svg className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <span>Back to Dashboard</span>
          </button>
          
          <div className="flex gap-3">
            {selectedTest?.confidence >= 95 ? (
              <button className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg font-semibold transition-all duration-300">
                <Award className="w-5 h-5" />
                Declare Winner
              </button>
            ) : (
              <button 
                disabled 
                className="flex items-center gap-3 px-6 py-3 bg-slate-300 text-slate-500 rounded-xl cursor-not-allowed font-semibold" 
                title="Need 95% confidence to declare winner"
              >
                <Award className="w-5 h-5" />
                Declare Winner
              </button>
            )}
            <button 
              onClick={() => handleUpdateExperimentStatus(selectedTest?.id, 'completed')}
              className="flex items-center gap-3 px-6 py-3 border-2 border-slate-300 rounded-xl text-slate-700 hover:bg-slate-50 font-semibold transition-colors"
            >
              <StopCircle className="w-5 h-5" />
              End Test
            </button>
            {(selectedTest?.status === 'running' || selectedTest?.status === 'active') ? (
              <button 
                onClick={() => handleUpdateExperimentStatus(selectedTest?.id, 'paused')}
                className="flex items-center gap-3 px-6 py-3 border-2 border-amber-300 text-amber-700 rounded-xl hover:bg-amber-50 font-semibold transition-colors"
              >
                <Pause className="w-5 h-5" />
                Pause Test
              </button>
            ) : (
              <button 
                onClick={() => handleUpdateExperimentStatus(selectedTest?.id, 'active')}
                className="flex items-center gap-3 px-6 py-3 border-2 border-emerald-300 text-emerald-700 rounded-xl hover:bg-emerald-50 font-semibold transition-colors"
              >
                <Play className="w-5 h-5" />
                Resume Test
              </button>
            )}
          </div>
        </div>

        {/* Enhanced Summary Bar */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <div className="grid grid-cols-5 gap-8">
            <div>
              <h3 className="font-bold text-2xl text-slate-900 mb-3">{selectedTest?.name}</h3>
              <StatusPill status={selectedTest?.status} />
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-slate-600 mb-1">Primary Metric</p>
              <p className="font-bold text-slate-900">{selectedTest?.metric}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-slate-600 mb-1">Participants</p>
              <p className="font-bold text-slate-900">{selectedTest?.totalParticipants?.toLocaleString() || 0}</p>
            </div>
            <div className="text-center">
              <div className="mb-3">
                <ConfidenceIndicator confidence={selectedTest?.confidence || 0} />
              </div>
              <p className="text-sm font-medium text-slate-600 mb-1">Confidence Level</p>
              <p className="font-bold text-slate-900">{selectedTest?.confidence || 0}%</p>
            </div>
            <div>
              {selectedTest?.leader && (
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Award className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-sm font-semibold text-emerald-900">
                      Variant {selectedTest.leader.variant} Leading
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-emerald-700">
                    +{selectedTest.leader.improvement}%
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Performance Chart */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="text-xl font-bold text-slate-900 mb-2">Performance Over Time</h4>
              <p className="text-slate-600">Track how each variant performs day by day</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span className="font-medium text-slate-700">Variant A</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-emerald-500 rounded-full"></div>
                <span className="font-medium text-slate-700">Variant B</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="day" 
                stroke="#64748b"
                fontSize={12}
                fontWeight={500}
              />
              <YAxis 
                stroke="#64748b"
                fontSize={12}
                fontWeight={500}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value, dataKey) => [
                  `${value}%`, 
                  dataKey === 'variantA' ? 'Variant A' : 'Variant B'
                ]}
              />
              <Line 
                type="monotone" 
                dataKey="variantA" 
                stroke="#3B82F6" 
                strokeWidth={4}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 6 }}
                name="Variant A"
              />
              <Line 
                type="monotone" 
                dataKey="variantB" 
                stroke="#10B981" 
                strokeWidth={4}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
                name="Variant B"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Enhanced Secondary Metrics */}
        <div className="grid grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <h4 className="text-xl font-bold text-slate-900 mb-6">Variant Comparison</h4>
            <div className="space-y-6">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-slate-900">Conversion Rate</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    (selectedTest?.variants?.b || 0) > (selectedTest?.variants?.a || 0) 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedTest?.variants?.a && selectedTest?.variants?.b
                      ? ((selectedTest.variants.b - selectedTest.variants.a) / selectedTest.variants.a * 100).toFixed(1)
                      : '0.0'
                    }% difference
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 text-center">
                    <div className="text-blue-600 font-semibold mb-1">Variant A</div>
                    <div className="text-2xl font-bold text-blue-700">
                      {selectedTest?.variants?.a?.toFixed(1) || '0.0'}%
                    </div>
                  </div>
                  <div className="bg-emerald-100 border border-emerald-200 rounded-lg p-4 text-center">
                    <div className="text-emerald-600 font-semibold mb-1">Variant B</div>
                    <div className="text-2xl font-bold text-emerald-700">
                      {selectedTest?.variants?.b?.toFixed(1) || '0.0'}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <h4 className="text-xl font-bold text-slate-900 mb-6">What Was Tested</h4>
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                    <span className="text-blue-700 font-bold">A</span>
                  </div>
                  <h5 className="font-bold text-blue-900">Variant A (Control)</h5>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg p-4 text-sm text-slate-700">
                  {selectedTest?.experiment_variants?.find(v => v.variant_name === 'A')?.configuration?.config || 
                   'Current live settings'}
                </div>
              </div>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-200 rounded-lg flex items-center justify-center">
                    <span className="text-emerald-700 font-bold">B</span>
                  </div>
                  <h5 className="font-bold text-emerald-900">Variant B (Test)</h5>
                </div>
                <div className="bg-white border border-emerald-200 rounded-lg p-4 text-sm text-slate-700">
                  {selectedTest?.experiment_variants?.find(v => v.variant_name === 'B')?.configuration?.config || 
                   'Test configuration'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading && activeView === 'overview') {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading Experiments</h3>
            <p className="text-slate-600">Fetching your A/B testing data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Route to different views
  if (activeView === 'create') {
    // Called as a function (not a component) so React reconciles the existing DOM
    // instead of unmounting/remounting — this preserves scroll position and input focus
    return CreateExperimentModal();
  }

  if (activeView === 'details' && selectedTest) {
    return ExperimentDetails();
  }

  // Enhanced Overview Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="p-8">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-4 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🧪</span>
              </div>
              A/B Testing
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Optimize your AI's performance with data-driven experiments
            </p>
          </div>
          
          <button 
            onClick={() => setActiveView('create')}
            className="group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-3 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <div>New Experiment</div>
              <div className="text-blue-100 text-sm opacity-90">Start optimizing today</div>
            </div>
          </button>
        </div>

        {/* ============================================
            GLOBAL ADMIN: Platform Experiments Monitor
            ============================================ */}
        {isGlobalAdmin && (
          <div className="mb-12 bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-indigo-900">Platform Experiments</h2>
                  <p className="text-xs text-indigo-600">Global admin view — read only</p>
                </div>
              </div>
              <AdminTenantFilterDropdown
                value={selectedAdminTenant}
                options={[
                  { value: 'all', label: 'All Tenants' },
                  ...adminTenants.map(t => ({ value: t.id, label: t.name }))
                ]}
                onChange={setSelectedAdminTenant}
              />
            </div>

            {/* Stats row */}
            {!adminLoading && adminExperiments.length > 0 && (
              <div className="grid grid-cols-3 divide-x divide-indigo-50 border-b border-indigo-50">
                {[
                  { label: 'Total', value: adminExperiments.length, color: 'text-slate-700' },
                  { label: 'Active', value: adminExperiments.filter(e => ['active','running'].includes(e.status)).length, color: 'text-emerald-600' },
                  { label: 'Completed', value: adminExperiments.filter(e => e.status === 'completed').length, color: 'text-slate-500' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="px-6 py-3 text-center">
                    <div className={`text-2xl font-bold ${color}`}>{value}</div>
                    <div className="text-xs text-slate-500 font-medium">{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Content */}
            {adminLoading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-500">Loading experiments...</p>
              </div>
            ) : adminExperiments.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-indigo-400" />
                </div>
                <p className="text-slate-600 font-medium">No experiments found</p>
                <p className="text-sm text-slate-400 mt-1">
                  {selectedAdminTenant === 'all' ? 'No tenants have created any experiments yet.' : 'This tenant has no experiments yet.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {/* Table header — fixed % widths via inline style for predictable layout */}
                <div className="flex px-6 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide gap-2">
                  <div style={{width:'18%'}}>Tenant</div>
                  <div style={{width:'26%'}}>Experiment</div>
                  <div style={{width:'14%'}}>Created By</div>
                  <div style={{width:'14%'}}>Status</div>
                  <div style={{width:'14%'}}>Type / Metric</div>
                  <div style={{width:'7%'}} className="text-center">Leads</div>
                  <div style={{width:'7%'}} className="text-center">Started</div>
                </div>
                {/* Rows */}
                {adminExperiments.map(exp => {
                  const creatorName = exp.creator
                    ? [exp.creator.first_name, exp.creator.last_name].filter(Boolean).join(' ') || exp.creator.email
                    : '—';
                  const startDate = exp.start_date
                    ? new Date(exp.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                    : exp.created_at
                      ? new Date(exp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                      : '—';
                  return (
                    <div
                      key={exp.id}
                      onClick={() => openAdminExpDetail(exp)}
                      className="flex px-6 py-3.5 items-center gap-2 hover:bg-indigo-50/40 cursor-pointer transition-colors text-sm group"
                    >
                      <div style={{width:'18%'}}>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium truncate max-w-full">
                          {exp.tenantName}
                        </span>
                      </div>
                      <div style={{width:'26%'}} className="min-w-0">
                        <div className="font-medium text-slate-900 truncate group-hover:text-indigo-700 transition-colors">{exp.name}</div>
                        {exp.campaignName && (
                          <div className="text-xs text-slate-400 truncate">{exp.campaignName}</div>
                        )}
                      </div>
                      <div style={{width:'14%'}} className="text-slate-500 text-xs truncate">{creatorName}</div>
                      <div style={{width:'14%'}}>
                        <StatusPill status={exp.status} />
                      </div>
                      <div style={{width:'14%'}} className="min-w-0">
                        <div className="text-slate-700 truncate capitalize text-xs">{exp.test_type || '—'}</div>
                        <div className="text-xs text-slate-400 truncate">{exp.primary_metric || '—'}</div>
                      </div>
                      <div style={{width:'7%'}} className="text-center text-slate-700 font-semibold">
                        {exp.participantCount}
                      </div>
                      <div style={{width:'7%'}} className="text-center text-xs text-slate-400">{startDate}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Read-only detail modal */}
            {selectedAdminExp && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedAdminExp(null)} />
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                  {/* Modal header */}
                  <div className="flex items-start justify-between p-6 border-b border-slate-100">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium">
                          {selectedAdminExp.tenantName}
                        </span>
                        <StatusPill status={selectedAdminExp.status} />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">{selectedAdminExp.name}</h3>
                      {selectedAdminExp.campaignName && (
                        <p className="text-sm text-slate-500 mt-0.5">{selectedAdminExp.campaignName}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedAdminExp(null)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600"
                    >
                      ✕
                    </button>
                  </div>
                  {/* Meta info */}
                  <div className="grid grid-cols-2 gap-4 p-6 border-b border-slate-100">
                    {[
                      { label: 'Created By', value: selectedAdminExp.creator ? [selectedAdminExp.creator.first_name, selectedAdminExp.creator.last_name].filter(Boolean).join(' ') || selectedAdminExp.creator.email : '—' },
                      { label: 'Test Type', value: selectedAdminExp.test_type || '—' },
                      { label: 'Metric', value: selectedAdminExp.primary_metric || '—' },
                      { label: 'Traffic Split', value: selectedAdminExp.traffic_split ? `${selectedAdminExp.traffic_split}% / ${100 - selectedAdminExp.traffic_split}%` : '50% / 50%' },
                      { label: 'Started', value: selectedAdminExp.start_date ? new Date(selectedAdminExp.start_date).toLocaleDateString() : selectedAdminExp.created_at ? new Date(selectedAdminExp.created_at).toLocaleDateString() : '—' },
                      { label: 'Total Leads', value: selectedAdminExp.participantCount },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
                        <div className="text-sm text-slate-800 font-medium">{value}</div>
                      </div>
                    ))}
                  </div>
                  {/* Variant results */}
                  <div className="p-6">
                    <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-indigo-500" />
                      Variant Performance
                    </h4>
                    {adminExpDetailLoading ? (
                      <div className="text-center py-8">
                        <div className="w-6 h-6 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {(selectedAdminExp.experiment_variants || []).map(v => {
                          const stats = adminExpDetail?.variantStats?.[v.variant_name];
                          const cfg = v.configuration || {};
                          return (
                            <div key={v.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-bold ${v.variant_name === 'A' ? 'bg-blue-500' : 'bg-purple-500'}`}>
                                    {v.variant_name}
                                  </div>
                                  <span className="font-semibold text-slate-800">Variant {v.variant_name}</span>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-slate-900">{stats?.total ?? 0}</div>
                                  <div className="text-xs text-slate-400">leads</div>
                                </div>
                              </div>
                              {stats?.avgMetric != null && (() => {
                                const raw = selectedAdminExp.primary_metric || 'metric';
                                const label = raw.charAt(0).toUpperCase() + raw.slice(1) + ' Rate';
                                return (
                                  <div className="text-sm text-slate-600 mb-2">
                                    {label}: <span className="font-semibold text-slate-800">{(stats.avgMetric * 100).toFixed(1)}%</span>
                                  </div>
                                );
                              })()}
                              {cfg.method && (
                                <div className="text-xs text-slate-500">Method: <span className="font-medium capitalize">{cfg.method}</span></div>
                              )}
                              {cfg.config && (
                                <div className="text-xs text-slate-400 mt-1 truncate">{cfg.config}</div>
                              )}
                            </div>
                          );
                        })}
                        {(!selectedAdminExp.experiment_variants || selectedAdminExp.experiment_variants.length === 0) && (
                          <p className="text-sm text-slate-400 text-center py-4">No variant data available.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Active Experiments Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Play className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Active Experiments</h2>
            {activeExperiments.length > 0 && (
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold">
                {activeExperiments.length} running
              </span>
            )}
          </div>
          
          {activeExperiments.length > 0 ? (
            <div className="grid gap-6 lg:grid-cols-2">
              {activeExperiments.map((experiment) => (
                <ExperimentCard key={experiment.id} experiment={experiment} />
              ))}
            </div>
          ) : (
            <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center group hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-300">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">No Active Experiments</h3>
              <p className="text-slate-600 mb-6 leading-relaxed max-w-md mx-auto">
                Start your first A/B test to optimize your lead conversion and boost performance
              </p>
              <button 
                onClick={() => setActiveView('create')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center gap-3 mx-auto font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Plus className="w-5 h-5" />
                Create Your First Test
              </button>
            </div>
          )}
        </div>

        {/* Enhanced Past Experiments Section */}
        {pastExperiments.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Past Experiments</h2>
              <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm font-semibold">
                {pastExperiments.length} completed
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastExperiments.map((experiment) => (
                <PastExperimentCard
                  key={experiment.id}
                  experiment={experiment}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ABTestingDashboard = (props) => (
  <ABTestingDashboardErrorBoundary>
    <ABTestingDashboardContent {...props} />
  </ABTestingDashboardErrorBoundary>
);

export default ABTestingDashboard;